import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { ColumnDefaultForm, SectionSpecificForm } from '@sites/entity-signal-form';

import type { NormalizedColumnEntity, NormalizedSpecificSectionEntity } from '@web/types';

import { CSS_RESTRICTIONS } from '@common/constants';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { WebColumnFormService } from '@web/core/services/web-column-form.service';
import { WebSectionFormService } from '@web/core/services/web-section-form.service';

import { SpInputNumComponent } from '@libs/components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpRadioGroupComponent } from '@libs/ui/sp-radio-group/src/sp-radio-group.component';
import { WebBackgroundImageSettingsComponent } from '@web/shared/components/web-background-image-settings/web-background-image-settings.component';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';

import { AbstractBaseSignalSettingsComponent } from '../abstract-base-settings/abstract-base-signal-settings.component';
import { SettingsBodyComponent } from '../settings-body/settings-body.component';
import { SettingsBreadcrumbsComponent } from '../settings-breadcrumbs/settings-breadcrumbs.component';
import type { BackgroundAtDevice, Side, VerticalPosition } from '@sites/entity-core';
import { backgroundAtDevice, toVerticalPosition, VERTICAL_ALIGN_BY_POSITION } from '@sites/entity-core';

type ColumnSlot = 'left' | 'center' | 'right';

/** The three fixed slots of a header row, in the order the panel lists them. */
const SLOTS = ['left', 'center', 'right'] as const satisfies readonly ColumnSlot[];

const SLOT_LABEL: Record<ColumnSlot, string> = {
    left: 'control_left',
    center: 'control_center',
    right: 'control_right',
};

const SLOT_HINT: Record<ColumnSlot, { show: string; hide: string }> = {
    left: { show: 'control_show_left_column', hide: 'control_hide_left_column' },
    center: { show: 'control_show_central_column', hide: 'control_hide_central_column' },
    right: { show: 'control_show_right_column', hide: 'control_hide_right_column' },
};

/** One row of the "columns in row" checkbox list. */
export type SlotToggle = {
    slot: ColumnSlot;
    label: string;
    hint: string;
    isVisible: boolean;
    isDisabled: boolean;
};

/**
 * Settings for a header/footer row. Edits go straight into the section signal
 * form (`WebSectionFormService`), which owns the dispatch and the history entry
 * — this panel only maps controls onto fields.
 */
@Component({
    selector: 'web-specific-section-settings',
    templateUrl: './specific-section-settings.component.html',
    styleUrls: ['./specific-section-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SettingsBodyComponent,
        SettingsBreadcrumbsComponent,
        TooltipModule,
        WebColorPickerComponent,
        WebBackgroundImageSettingsComponent,
        SpInputNumComponent,
        SpRadioGroupComponent,
        TranslateModule,
    ],
})
export class SpecificSectionSettingsComponent extends AbstractBaseSignalSettingsComponent implements OnInit {
    public readonly entity = input.required<NormalizedSpecificSectionEntity>();

    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly columnFormService = inject(WebColumnFormService);
    private readonly sectionFormService = inject(WebSectionFormService);
    private readonly destroyRef = inject(DestroyRef);

    private form!: SectionSpecificForm;

    private readonly columnsEntities = toSignal(this.columnsFacade.columnsEntities$, { initialValue: {} });

    public readonly radioGroupType = ERadioGroupType;

    public readonly PADDING_MIN = CSS_RESTRICTIONS.BLOCK_PADDING_MIN;
    public readonly PADDING_MAX = CSS_RESTRICTIONS.BLOCK_PADDING_MAX;

    public ngOnInit(): void {
        this.form = this.sectionFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as SectionSpecificForm;
    }

    // --- Reads ----------------------------------------------------------------

    // Every read below is a single FieldTree leaf — the v0 form exposes them one
    // by one, so a padding edit no longer wakes the background readers.
    public readonly padding = computed(() => this.form.padding().value()?.value ?? null);
    public readonly columnGap = computed<number>(() => Number(this.form.columnGap().value() ?? 0));
    /** In the letters `sp-radio-group` speaks; the section stores the words. */
    public readonly verticalPosition = computed<string>(
        () => toVerticalPosition(this.form.verticalPosition().value()) ?? ''
    );

    public readonly backgroundColor = computed<string>(() => {
        const color = this.form.backgroundColor().value();

        return color ?? '';
    });

    /** The section's background is flat — one for every screen — so it reads as desktop alone. */
    public readonly background = computed(() =>
        backgroundAtDevice(
            {
                image: { desktop: this.form.backgroundImage().value() ?? null },
                size: { desktop: this.form.backgroundSize().value() ?? null },
                position: { desktop: this.form.backgroundPosition().value() ?? null },
                repeat: { desktop: this.form.backgroundRepeat().value() ?? null },
            },
            'desktop'
        )
    );

    private readonly visibleSlots = computed<Record<ColumnSlot, boolean>>(() => ({
        left: Boolean(this.form.showLeftColumn().value()),
        center: Boolean(this.form.showCenterColumn().value()),
        right: Boolean(this.form.showRightColumn().value()),
    }));

    public readonly visibleColumnsCount = computed<number>(
        () => Object.values(this.visibleSlots()).filter(Boolean).length
    );

    /**
     * The three checkboxes, ready to render. The last visible slot cannot be
     * hidden — a row with no columns has nothing to select or drop into.
     */
    public readonly slotToggles = computed<SlotToggle[]>(() => {
        const visible = this.visibleSlots();
        const isLastVisible = this.visibleColumnsCount() === 1;

        return SLOTS.map((slot) => ({
            slot,
            label: SLOT_LABEL[slot],
            isVisible: visible[slot],
            isDisabled: visible[slot] && isLastVisible,
            hint:
                visible[slot] && isLastVisible
                    ? 'control_hide_column_unavailable'
                    : SLOT_HINT[slot][visible[slot] ? 'hide' : 'show'],
        }));
    });

    // --- Writes ---------------------------------------------------------------

    public setBackground({ image, size, position, repeat }: BackgroundAtDevice): void {
        // One transaction so the picker's layout and the file land as a single
        // change — the panel used to suppress the layout's own emission.
        this.form.transact(() => {
            this.form.setBackgroundImage(image as never);
            this.form.setBackgroundSize(size ?? '');
            this.form.setBackgroundPosition(position ?? '');
            this.form.setBackgroundRepeat(repeat ?? '');
        });
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form.setBackgroundColor(presetName ?? value ?? null);
    }

    public setAllPaddings(value: number): void {
        this.form.setPadding({ top: value, bottom: value, left: value, right: value });
    }

    public setPadding(side: Side, value: number): void {
        this.form.setPadding({ [side]: value });
    }

    public setColumnGap(value: number): void {
        this.form.setColumnGap(value);
    }

    public setVerticalPosition(value: string): void {
        const position = VERTICAL_ALIGN_BY_POSITION[value as VerticalPosition];

        if (position) {
            this.form.setVerticalPosition(position);
        }
    }

    /**
     * Hiding or showing a slot re-splits the row: the remaining columns share
     * the 12-column grid evenly.
     */
    public toggleSlot(slot: ColumnSlot, isVisible: boolean): void {
        const form = this.form;

        if (slot === 'left') form.setShowLeftColumn(isVisible);
        if (slot === 'center') form.setShowCenterColumn(isVisible);
        if (slot === 'right') form.setShowRightColumn(isVisible);

        this.resetColumnWidths();
    }

    /**
     * Each width goes through that column's own form rather than the store, so
     * the re-split joins the same history batch as the toggle that caused it.
     */
    private resetColumnWidths(): void {
        const entities = this.columnsEntities();
        const value = 12 / this.visibleColumnsCount();

        (this.entity()?.children ?? []).forEach((id: string) => {
            const column = entities[id] as NormalizedColumnEntity | undefined;

            if (!column || !('width' in column.props)) {
                return;
            }

            // Scoped to the panel: the form has to outlive this call, since it
            // dispatches its update from an effect rather than inline.
            const form = this.columnFormService.getOrCreateScoped(id, this.destroyRef) as ColumnDefaultForm;

            form.setWidth(value);
        });
    }
}
